import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import api from '../services/api';
import { imageService } from '../services';

const PAGE_SIZE = 12;

// Hàm rút gọn mô tả
function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  const truncated = text.slice(0, maxLength);
  return truncated.slice(0, truncated.lastIndexOf(' ')) + '...';
}

const GameReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [size] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  // Lấy keyword từ query string
  const params = new URLSearchParams(location.search);
  const keyword = params.get('keyword') || '';

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        let url = `/game-reviews/all?page=${page}&size=${size}`;
        if (keyword) url += `&keyword=${encodeURIComponent(keyword)}`;
        const res = await api.get(url);
        setReviews(res?.data?.content || res?.content || []);
        setTotalPages(res?.data?.totalPages || res?.totalPages || 1);
        setTotalElements(res?.data?.totalElements || res?.totalElements || 0);
      } catch (err) {
        setError('Failed to load game reviews.');
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [page, size, keyword]);

  useEffect(() => {
    document.title = 'Game Lim - Review Game';
  }, []);

  // Render star rating
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} style={{ color: '#fbbf24', fontSize: 20 }}>★</span>
        ))}
        {hasHalfStar && <span style={{ color: '#fbbf24', fontSize: 20 }}>★</span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} style={{ color: '#e5e7eb', fontSize: 20 }}>★</span>
        ))}
      </div>
    );
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setPage(i)}
          style={{
            margin: '0 4px',
            padding: '6px 14px',
            borderRadius: 6,
            border: i === page ? '2px solid #06beb6' : '1px solid #eee',
            background: i === page ? 'linear-gradient(90deg, #06beb6 0%, #fa709a 100%)' : '#fff',
            color: i === page ? '#fff' : '#222',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.18s',
          }}
          disabled={i === page}
        >
          {i}
        </button>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 32, gap: 8 }}>
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          style={{ marginRight: 8, padding: '6px 14px', borderRadius: 6, border: '1px solid #eee', background: '#fff', color: '#222', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
        >Prev</button>
        {pages}
        <button
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
          style={{ marginLeft: 8, padding: '6px 14px', borderRadius: 6, border: '1px solid #eee', background: '#fff', color: '#222', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
        >Next</button>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 16px' }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 32, textAlign: 'left' }}>Game Review</h1>
        {loading && <div>Loading...</div>}
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 28,
        }}>
          {reviews.length === 0 && keyword ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#bfa16c', fontSize: 22, fontWeight: 600, padding: '48px 0' }}>
              No results for <span style={{ color: '#ad1457' }}>&quot;{keyword}&quot;</span>
            </div>
          ) : (
            reviews.map((item, idx) => (
              <div
                key={item.id || idx}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 340,
                  position: 'relative',
                  transition: 'box-shadow 0.2s',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/review-game/${item.slug}`)}
              >
                <img
                  src={item.thumbnailPath ? imageService.getThumbnail(item.thumbnailPath) : (item.image || 'https://via.placeholder.com/400x220?text=No+Image')}
                  alt={item.title}
                  style={{ width: '100%', height: 180, objectFit: 'cover', background: '#eee' }}
                />
                <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 12px 0', color: '#222', textAlign: 'left' }}>{item.title}</h2>
                  <div style={{ color: '#666', fontSize: 16, marginBottom: 16, textAlign: 'left' }}>
                    {truncateText(item.description || item.shortDescription || '', 110)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {typeof item.rateAverage === 'number' && renderStars(item.rateAverage)}
                    <button
                      style={{
                        padding: '10px 22px',
                        background: 'linear-gradient(90deg, #06beb6 0%, #fa709a 100%)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 15,
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(6,190,182,0.12)',
                        transition: 'background 0.2s',
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/review-game/${item.slug}`);
                      }}
                    >
                      Read full review
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {renderPagination()}
        <div style={{ marginTop: 18, color: '#888', fontSize: 15, textAlign: 'right' }}>
          Total: {totalElements} reviews
        </div>
      </div>
    </>
  );
};

export default GameReviews; 